import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "United States",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  phone: "",
  shippingMethod: "standard",
  cardNumber: "",
  expiry: "",
  security: "",
  cardName: "",
};

function CheckOut() {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const discount = Math.min(Math.max(Number(location.state?.discount) || 0, 0), 0.5);
  const resolvedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = resolvedItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const discountAmount = subtotal * discount;
  const discountedSubtotal = subtotal - discountAmount;
  const standardShipping = discountedSubtotal >= siteConfig.freeShippingThreshold ? 0 : siteConfig.shippingFlatRate;
  const shipping = form.shippingMethod === "express" ? 28 : standardShipping;
  const total = discountedSubtotal + shipping;

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const requiredFields = [
      "email",
      "firstName",
      "lastName",
      "address",
      "city",
      "region",
      "postalCode",
      "phone",
      "cardNumber",
      "expiry",
      "security",
      "cardName",
    ];

    requiredFields.forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (form.cardNumber.replace(/\s/g, "").length !== 16) {
      nextErrors.cardNumber = "Enter a 16-digit card number for this demo.";
    }
    if (form.expiry && !/^(0[1-9]|1[0-2])\s?\/\s?\d{2}$/.test(form.expiry)) {
      nextErrors.expiry = "Use MM / YY format.";
    } else if (form.expiry) {
      const [month, year] = form.expiry.split("/").map((part) => Number(part.trim()));
      const expiryDate = new Date(2000 + year, month, 0, 23, 59, 59);
      if (expiryDate < new Date()) nextErrors.expiry = "Use a card with a future expiration date.";
    }
    if (form.security && !/^\d{3,4}$/.test(form.security)) {
      nextErrors.security = "Enter a 3 or 4-digit security code.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstError = document.getElementById(Object.keys(nextErrors)[0]);
      firstError?.focus();
      return;
    }

    setIsSubmitting(true);
    const orderNumber = `${siteConfig.brandName.slice(0, 2).toUpperCase()}-${Date.now().toString().slice(-7)}`;
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: { orderNumber, email: form.email, total },
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 700);
  };

  const handleCardNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 16);
    setField("cardNumber", digits.replace(/(.{4})/g, "$1 ").trim());
  };

  const handleExpiry = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    setField("expiry", digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits);
  };

  if (resolvedItems.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty">
        <span className="eyebrow">Checkout</span>
        <h1>Your cart is empty.</h1>
        <p>Add something from the collection before continuing to checkout.</p>
        <Link className="button button--accent" to="/shop">Return to shop</Link>
      </main>
    );
  }

  const fieldError = (name) => (errors[name] ? <p className="checkout-page__error" role="alert">{errors[name]}</p> : null);

  return (
    <main className="checkout-page">
      <div className="checkout-page__heading page-width">
        <Link to="/cartpage"><FiArrowLeft aria-hidden="true" /> Return to cart</Link>
        <div>
          <span className="eyebrow">Secure checkout</span>
          <h1>Complete your order</h1>
        </div>
      </div>

      <form className="checkout-page__layout page-width" onSubmit={handleSubmit} noValidate>
        <div className="checkout-page__form">
          <section>
            <div className="checkout-page__section-title"><span>01</span><h2>Contact</h2></div>
            <label>
              <span>Email address</span>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
                aria-invalid={Boolean(errors.email)}
              />
              {fieldError("email")}
            </label>
          </section>

          <section>
            <div className="checkout-page__section-title"><span>02</span><h2>Delivery</h2></div>
            <label>
              <span>Country / region</span>
              <select id="country" value={form.country} onChange={(event) => setField("country", event.target.value)}>
                <option>United States</option>
                <option>Canada</option>
                <option>United Kingdom</option>
                <option>Australia</option>
                <option>Japan</option>
                <option>Other</option>
              </select>
            </label>
            <div className="checkout-page__form-row">
              <label>
                <span>First name</span>
                <input id="firstName" autoComplete="given-name" value={form.firstName} onChange={(event) => setField("firstName", event.target.value)} aria-invalid={Boolean(errors.firstName)} />
                {fieldError("firstName")}
              </label>
              <label>
                <span>Last name</span>
                <input id="lastName" autoComplete="family-name" value={form.lastName} onChange={(event) => setField("lastName", event.target.value)} aria-invalid={Boolean(errors.lastName)} />
                {fieldError("lastName")}
              </label>
            </div>
            <label>
              <span>Company <small>Optional</small></span>
              <input id="company" autoComplete="organization" value={form.company} onChange={(event) => setField("company", event.target.value)} />
            </label>
            <label>
              <span>Address</span>
              <input id="address" autoComplete="address-line1" value={form.address} onChange={(event) => setField("address", event.target.value)} aria-invalid={Boolean(errors.address)} />
              {fieldError("address")}
            </label>
            <label>
              <span>Apartment, suite, etc. <small>Optional</small></span>
              <input id="apartment" autoComplete="address-line2" value={form.apartment} onChange={(event) => setField("apartment", event.target.value)} />
            </label>
            <div className="checkout-page__form-row checkout-page__form-row--three">
              <label>
                <span>City</span>
                <input id="city" autoComplete="address-level2" value={form.city} onChange={(event) => setField("city", event.target.value)} aria-invalid={Boolean(errors.city)} />
                {fieldError("city")}
              </label>
              <label>
                <span>State / region</span>
                <input id="region" autoComplete="address-level1" value={form.region} onChange={(event) => setField("region", event.target.value)} aria-invalid={Boolean(errors.region)} />
                {fieldError("region")}
              </label>
              <label>
                <span>Postal code</span>
                <input id="postalCode" autoComplete="postal-code" value={form.postalCode} onChange={(event) => setField("postalCode", event.target.value)} aria-invalid={Boolean(errors.postalCode)} />
                {fieldError("postalCode")}
              </label>
            </div>
            <label>
              <span>Phone</span>
              <input id="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(event) => setField("phone", event.target.value)} aria-invalid={Boolean(errors.phone)} />
              {fieldError("phone")}
            </label>
          </section>

          <section>
            <div className="checkout-page__section-title"><span>03</span><h2>Shipping method</h2></div>
            <label className={`checkout-page__shipping-option${form.shippingMethod === "standard" ? " is-selected" : ""}`}>
              <input type="radio" name="shippingMethod" value="standard" checked={form.shippingMethod === "standard"} onChange={(event) => setField("shippingMethod", event.target.value)} />
              <span><strong>Ground shipping</strong><small>3–6 business days</small></span>
              <b>{standardShipping === 0 ? "Free" : `${siteConfig.currencySymbol}${standardShipping.toFixed(2)}`}</b>
            </label>
            <label className={`checkout-page__shipping-option${form.shippingMethod === "express" ? " is-selected" : ""}`}>
              <input type="radio" name="shippingMethod" value="express" checked={form.shippingMethod === "express"} onChange={(event) => setField("shippingMethod", event.target.value)} />
              <span><strong>Express shipping</strong><small>1–2 business days</small></span>
              <b>{siteConfig.currencySymbol}28.00</b>
            </label>
          </section>

          <section>
            <div className="checkout-page__section-title"><span>04</span><h2>Payment</h2></div>
            <p className="checkout-page__demo-note"><FiLock aria-hidden="true" /> Demo checkout — no payment is processed or stored.</p>
            <div className="checkout-page__payment-head">
              <strong>Credit card</strong><span>VISA</span><span>MC</span><span>AMEX</span>
            </div>
            <div className="checkout-page__payment-fields">
              <label>
                <span>Card number</span>
                <input id="cardNumber" inputMode="numeric" autoComplete="cc-number" placeholder="0000 0000 0000 0000" value={form.cardNumber} onChange={(event) => handleCardNumber(event.target.value)} aria-invalid={Boolean(errors.cardNumber)} />
                {fieldError("cardNumber")}
              </label>
              <div className="checkout-page__form-row">
                <label>
                  <span>Expiration date</span>
                  <input id="expiry" inputMode="numeric" autoComplete="cc-exp" placeholder="MM / YY" value={form.expiry} onChange={(event) => handleExpiry(event.target.value)} aria-invalid={Boolean(errors.expiry)} />
                  {fieldError("expiry")}
                </label>
                <label>
                  <span>Security code</span>
                  <input id="security" inputMode="numeric" autoComplete="cc-csc" placeholder="CVV" value={form.security} onChange={(event) => setField("security", event.target.value.replace(/\D/g, "").slice(0, 4))} aria-invalid={Boolean(errors.security)} />
                  {fieldError("security")}
                </label>
              </div>
              <label>
                <span>Name on card</span>
                <input id="cardName" autoComplete="cc-name" value={form.cardName} onChange={(event) => setField("cardName", event.target.value)} aria-invalid={Boolean(errors.cardName)} />
                {fieldError("cardName")}
              </label>
            </div>
          </section>
        </div>

        <aside className="checkout-page__summary">
          <span className="eyebrow">Order summary</span>
          <h2>{resolvedItems.reduce((count, item) => count + item.quantity, 0)} items</h2>
          <div className="checkout-page__summary-items">
            {resolvedItems.map((item) => (
              <div key={`${item.id}-${item.size}-${item.color}`}>
                <div className="checkout-page__summary-image">
                  <img src={item.product.image} alt="" width="1086" height="1448" />
                  <span>{item.quantity}</span>
                </div>
                <div><strong>{item.product.name}</strong><small>{item.color} · {item.size}</small></div>
                <b>{siteConfig.currencySymbol}{(item.product.price * item.quantity).toFixed(2)}</b>
              </div>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{siteConfig.currencySymbol}{subtotal.toFixed(2)}</dd></div>
            {discount > 0 && <div className="is-discount"><dt>Discount</dt><dd>−{siteConfig.currencySymbol}{discountAmount.toFixed(2)}</dd></div>}
            <div><dt>Shipping</dt><dd>{shipping === 0 ? "Free" : `${siteConfig.currencySymbol}${shipping.toFixed(2)}`}</dd></div>
            <div className="checkout-page__total"><dt>Total <small>{siteConfig.currency}</small></dt><dd>{siteConfig.currencySymbol}{total.toFixed(2)}</dd></div>
          </dl>
          <button className="button button--accent" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Placing order…" : `Pay ${siteConfig.currencySymbol}${total.toFixed(2)}`}
          </button>
          <p>By placing your order, you agree to our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.</p>
        </aside>
      </form>
    </main>
  );
}

export default CheckOut;
